import { useEffect, useState } from 'react';
import { useReduxState } from 'hooks';
import { getBlockFileForLanguage } from 'lib/courses';
import { waitForAudio } from 'lib/files';
import { Audio } from 'components/common';
import { FileUploader } from 'components/common/uploader';
import { LoadingIndicator } from 'components/common';
import {
  defaultBlockData,
  EditorBlockCustomControls,
  EditorBlockTranslationReplace
} from 'components/editor/blocks';
import { EditorBlockEmbedFileControls } from 'components/editor/blocks/embed';

import type { BlockAudio } from 'shared-types';
import type { BlockProps } from 'components/editor/blocks/EditorBlock';
import type { EditorStateProps } from 'components/editor/blocks/EditorBlock';

type StateProps = Pick<EditorStateProps, 'editorLanguageActive'>;

const EditorBlockAudio = ({
  blockId,
  data,
  languageVersion,
  updateData,
  ...stateProps
}: BlockProps<BlockAudio> & StateProps) => {
  const { editorLanguageActive } = stateProps;

  const {
    messages,
    pageState: { course },
    theme
  } = useReduxState();

  const [audioVisible, setAudioVisible] = useState(false);
  const [renderAudio, setRenderAudio] = useState(true);
  const [transcoding, setTranscoding] = useState(false);
  const [showLanguageSpecificUploader, setShowLanguageSpecificUploader] =
    useState(false);

  const {
    activeLanguage,
    shouldShowDefaultFile,
    fileUrl: audioUrl
  } = getBlockFileForLanguage({
    data,
    courseLanguage: course.language,
    editorActiveLanguageVersion: editorLanguageActive,
    showLanguageSpecificUploader
  });

  useEffect(() => {
    initAudio(audioUrl);
  }, [audioUrl]);

  const initAudio = async (url: string) => {
    if (!url) return;

    if (url.indexOf('/transcoded/') !== -1) {
      await waitForAudio({ onError: () => setTranscoding(true), url });
    }

    setTranscoding(false);
    setAudioVisible(true);
  };

  const handleUploadSuccess = ({ extension, url }) => {
    let transcodedUrl = extension ? url.slice(0, url.lastIndexOf('.')) : url;
    transcodedUrl = `${transcodedUrl.replace(
      '/original/',
      '/transcoded/'
    )}.mp3`;

    updateData({
      ...data,
      locales: {
        ...data.locales,
        [languageVersion]: {
          ...data.locales[languageVersion],
          file: { url: transcodedUrl }
        }
      }
    });

    setShowLanguageSpecificUploader(false);
    setTranscoding(true);
    initAudio(transcodedUrl);
  };

  const handleCancel = () => {
    setShowLanguageSpecificUploader(false);
  };

  const handleReplace = () => {
    setShowLanguageSpecificUploader(true);
  };

  const handleRemove = () => {
    setRenderAudio(false);
    updateData({
      ...data,
      locales: {
        ...data.locales,
        [languageVersion]: {
          ...defaultBlockData.audio.locales[languageVersion]
        }
      }
    });
    setTimeout(() => setRenderAudio(true));
  };

  return (
    <div className="audio">
      {!audioUrl || transcoding || showLanguageSpecificUploader ? (
        <div>
          <FileUploader
            allowedFileTypes={['audio/*']}
            autoProceed={true}
            dragDrop={true}
            icon="file-audio"
            id={`audio-uploader-${blockId}`}
            inProgress={transcoding}
            inProgressText={messages.uploaderTranscoding}
            keyPrefix="uploads/audios/original/"
            onUploadSuccess={handleUploadSuccess}
            placeholder={messages.editorBlockAudioChooseFile}
            style={
              editorLanguageActive
                ? {
                    borderRadius: `${theme.cardBorderRadius} ${theme.cardBorderRadius} 0 0`
                  }
                : {}
            }
            uuidAsFilename={true}
          />
        </div>
      ) : (
        <>
          <div style={{ display: audioVisible ? 'none' : 'block' }}>
            <LoadingIndicator
              borderWidth={2}
              color={theme.textColorLighter}
              size={20}
              style={{ padding: 10 }}
            />
          </div>

          <div style={{ display: audioVisible ? 'block' : 'none' }}>
            {renderAudio ? (
              <Audio
                style={
                  editorLanguageActive
                    ? {
                        borderRadius: `${theme.cardBorderRadius} ${theme.cardBorderRadius} 0 0`
                      }
                    : {}
                }
                url={
                  shouldShowDefaultFile
                    ? data.locales[course.language]?.file?.url
                    : audioUrl
                }
              />
            ) : null}
          </div>

          {audioUrl && !transcoding && !editorLanguageActive ? (
            <EditorBlockCustomControls>
              <EditorBlockEmbedFileControls
                file={{ url: data.locales[activeLanguage]?.file?.url }}
                type="audio"
              />
            </EditorBlockCustomControls>
          ) : null}
        </>
      )}

      {editorLanguageActive ? (
        <EditorBlockTranslationReplace
          customStatusText={messages.blocksAudioUsingLanguageSpecific}
          defaultStatusText={messages.blocksAudioUsingDefaultLanguage}
          downloadableFileUrl={data.locales[activeLanguage]?.file?.url}
          hasCustomValue={!shouldShowDefaultFile}
          icon="play-circle"
          onCancel={handleCancel}
          onRemove={handleRemove}
          onReplace={handleReplace}
          showCancel={showLanguageSpecificUploader}
        />
      ) : null}
    </div>
  );
};

export default EditorBlockAudio;
